import { useEffect, useState } from "react";
import { motion, animate } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Reveal, Overline } from "./Reveal";

export const LiveCounter = ({ count, light = true }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (count == null) return;
    const controls = animate(display, count, {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);
  return (
    <p data-testid="live-counter" className={`font-mono-alt text-xs sm:text-sm uppercase tracking-[0.25em] ${light ? "text-[#FAF8F5]/70" : "text-[#736F6E]"}`}>
      <span data-testid="live-counter-number" className={`font-display text-2xl sm:text-3xl tracking-tight mr-3 ${light ? "text-[#CBD5C0]" : "text-[#0D0D0D]"}`}>
        {display}
      </span>
      personnes suivent déjà le lancement SOLAMOVE
    </p>
  );
};

const inputClass =
  "w-full bg-transparent border-b border-white/20 focus:border-[#CBD5C0] outline-none py-4 text-[#FAF8F5] placeholder:text-[#FAF8F5]/35 transition-colors duration-500 text-base";

export const Reserve = () => {
  const [count, setCount] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    shoe_size: "", preference: "", marketing_consent: false, privacy_accepted: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [serverError, setServerError] = useState(null);

  const fetchCount = async () => {
    try {
      const { data } = await api.get("/prereservations/count");
      setCount(data.count);
    } catch {
      setCount(null);
    }
  };

  useEffect(() => { fetchCount(); }, []);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Prénom requis";
    if (!form.last_name.trim()) e.last_name = "Nom requis";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email invalide";
    if (!form.shoe_size) e.shoe_size = "Sélectionnez votre pointure";
    if (!form.privacy_accepted) e.privacy_accepted = "L'acceptation de la politique de confidentialité est obligatoire";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setServerError(null);
    setLoading(true);
    try {
      const { data } = await api.post("/prereservations", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        shoe_size: parseInt(form.shoe_size, 10),
        preference: form.preference || null,
        marketing_consent: form.marketing_consent,
        privacy_accepted: form.privacy_accepted,
        website: form.website,
      });
      setSuccess(data.message);
      setCount(data.count);
      trackEvent("Lead", { content_name: "prereservation", shoe_size: form.shoe_size, preference: form.preference || "none" });
      toast.success("Pré-réservation enregistrée");
    } catch (err) {
      const msg = formatApiError(err);
      setServerError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reserver" data-testid="reserve-section" className="py-28 sm:py-40 bg-[#0D0D0D] text-[#FAF8F5]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal><Overline light>Pré-réservation</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display uppercase tracking-tight leading-[1.02] text-4xl sm:text-6xl mt-6">
              Soyez parmi les premiers à marcher avec SOLAMOVE.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex items-end gap-5">
              <span data-testid="reserve-price" className="font-display font-light leading-none text-6xl sm:text-8xl">0&nbsp;€</span>
              <span className="font-mono-alt text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#CBD5C0] pb-2 sm:pb-4">
                Pré-réservation<br />gratuite
              </span>
            </div>
            <p className="mt-8 text-[#FAF8F5]/60 text-base sm:text-lg leading-relaxed max-w-md">
              Sans engagement. Aucun paiement aujourd'hui.
            </p>
            <p className="mt-4 text-[#FAF8F5]/60 text-base sm:text-lg leading-relaxed max-w-md">
              La pré-réservation vous donne simplement un accès prioritaire aux informations
              concernant le lancement de SOLAMOVE.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-14 border-t border-white/10 pt-8">
              <LiveCounter count={count} />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          {success ? (
            <motion.div
              data-testid="prereservation-success"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="border border-[#CBD5C0]/40 p-10 sm:p-14 h-full flex flex-col justify-center"
            >
              <span className="h-12 w-12 rounded-full bg-[#CBD5C0] flex items-center justify-center mb-8">
                <Check size={22} className="text-[#0D0D0D]" />
              </span>
              <h3 className="font-display uppercase tracking-tight text-3xl sm:text-4xl">Votre place est réservée.</h3>
              <p className="mt-6 text-[#FAF8F5]/70 leading-relaxed">
                Vous serez informé(e) en priorité de l'ouverture des commandes SOLAMOVE.
                Un email de confirmation vient de vous être envoyé.
              </p>
              <p className="mt-10 font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#CBD5C0]">
                Every step makes you stronger.
              </p>
            </motion.div>
          ) : (
            <form data-testid="prereservations-form" onSubmit={submit} noValidate className="space-y-2">
              <div className="grid sm:grid-cols-2 gap-x-8">
                <div>
                  <input data-testid="form-input-firstname" type="text" placeholder="Prénom *" value={form.first_name}
                    onChange={(e) => set("first_name", e.target.value)} className={inputClass} autoComplete="given-name" />
                  {errors.first_name && <p className="text-[#E6CECA] text-xs mt-2">{errors.first_name}</p>}
                </div>
                <div>
                  <input data-testid="form-input-lastname" type="text" placeholder="Nom *" value={form.last_name}
                    onChange={(e) => set("last_name", e.target.value)} className={inputClass} autoComplete="family-name" />
                  {errors.last_name && <p className="text-[#E6CECA] text-xs mt-2">{errors.last_name}</p>}
                </div>
              </div>
              <div>
                <input data-testid="form-input-email" type="email" placeholder="Email *" value={form.email}
                  onChange={(e) => set("email", e.target.value)} className={inputClass} autoComplete="email" />
                {errors.email && <p className="text-[#E6CECA] text-xs mt-2">{errors.email}</p>}
              </div>
              <input data-testid="form-input-phone" type="tel" placeholder="Téléphone (facultatif)" value={form.phone}
                onChange={(e) => set("phone", e.target.value)} className={inputClass} autoComplete="tel" />
              <div>
                <select
                  data-testid="form-select-shoesize"
                  value={form.shoe_size}
                  onChange={(e) => set("shoe_size", e.target.value)}
                  className={`${inputClass} appearance-none cursor-pointer ${form.shoe_size ? "" : "text-[#FAF8F5]/35"}`}
                >
                  <option value="" disabled className="bg-[#0D0D0D]">Pointure (EU) *</option>
                  {Array.from({ length: 14 }, (_, i) => 35 + i).map((s) => (
                    <option key={s} value={s} className="bg-[#0D0D0D] text-[#FAF8F5]">{s}</option>
                  ))}
                </select>
                {errors.shoe_size && <p className="text-[#E6CECA] text-xs mt-2">{errors.shoe_size}</p>}
              </div>

              <div className="pt-6">
                <p className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#FAF8F5]/50 mb-4">Préférence (facultatif)</p>
                <div className="flex gap-3 flex-wrap">
                  {[["femme", "Femme"], ["homme", "Homme"], ["unisexe", "Unisexe"]].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`form-radio-gender-${val}`}
                      onClick={() => set("preference", form.preference === val ? "" : val)}
                      className={`px-6 py-3 rounded-full border text-xs uppercase tracking-[0.15em] transition-all duration-500 ${
                        form.preference === val
                          ? "border-[#CBD5C0] bg-[#CBD5C0] text-[#0D0D0D]"
                          : "border-white/20 text-[#FAF8F5]/60 hover:border-white/50"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)}
                tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

              <div className="pt-8 space-y-5">
                <label className="flex items-start gap-4 cursor-pointer group">
                  <input
                    data-testid="form-checkbox-privacy"
                    type="checkbox"
                    checked={form.privacy_accepted}
                    onChange={(e) => set("privacy_accepted", e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 appearance-none border border-white/30 checked:bg-[#CBD5C0] checked:border-[#CBD5C0] transition-colors duration-300 cursor-pointer"
                  />
                  <span className="text-sm text-[#FAF8F5]/60 leading-relaxed">
                    J'accepte la{" "}
                    <a href="/politique-de-confidentialite" target="_blank" className="underline underline-offset-4 text-[#FAF8F5]/90 hover:text-[#CBD5C0] transition-colors">
                      politique de confidentialité
                    </a>{" "}
                    de SOLAMOVE. *
                  </span>
                </label>
                {errors.privacy_accepted && <p className="text-[#E6CECA] text-xs">{errors.privacy_accepted}</p>}
                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    data-testid="form-checkbox-marketing"
                    type="checkbox"
                    checked={form.marketing_consent}
                    onChange={(e) => set("marketing_consent", e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 appearance-none border border-white/30 checked:bg-[#CBD5C0] checked:border-[#CBD5C0] transition-colors duration-300 cursor-pointer"
                  />
                  <span className="text-sm text-[#FAF8F5]/60 leading-relaxed">
                    Je souhaite recevoir les actualités et les coulisses du développement SOLAMOVE (facultatif).
                  </span>
                </label>
              </div>

              <div className="pt-8">
                <button
                  data-testid="form-submit-button"
                  type="submit"
                  disabled={loading}
                  className="group relative w-full overflow-hidden bg-[#FAF8F5] text-[#0D0D0D] font-display uppercase tracking-[0.15em] text-sm px-10 py-6 rounded-full disabled:opacity-60"
                >
                  <span className="relative z-10 flex items-center justify-center gap-3 transition-colors duration-500 group-hover:text-[#FAF8F5]">
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? "Enregistrement..." : "Je pré-réserve SOLAMOVE"}
                  </span>
                  <span className="absolute inset-0 bg-[#CBD5C0] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                </button>
                {serverError && (
                  <p data-testid="form-server-error" className="mt-5 text-center text-[#E6CECA] text-sm leading-relaxed">{serverError}</p>
                )}
                <p className="mt-6 text-center font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#FAF8F5]/50 leading-relaxed">
                  0 € • Sans engagement • Aucun paiement aujourd'hui.
                </p>
                <p className="mt-3 text-center text-[11px] text-[#FAF8F5]/35 leading-relaxed max-w-sm mx-auto">
                  La pré-réservation ne constitue pas une commande. Elle donne simplement un accès prioritaire aux informations sur le lancement.
                </p>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
